/**
 * Turn Art Editor - View/edit inputs and regenerate illustrations
 * 
 * Allows agents to:
 * - View Turn Art inputs (scene description, story bible, source image)
 * - Edit scene descriptions to guide illustration generation
 * - Regenerate individual pages or all pages
 * - View Story Bible for character consistency
 * 
 * @see lib/turn-illustration for generation logic
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Wand2,
  Image as ImageIcon,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Loader2,
  Check,
  AlertCircle,
  Users,
  MapPin,
  Palette,
  Eye,
  Edit2,
  Save,
  X,
} from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { FEATURES } from '@/lib/feature-flags';
import { glassStyles } from '@/lib/design';
import {
  generateTurnIllustration,
  getOrCreateStoryBible,
  clearStoryBibleCache,
  type BookStoryBible,
} from '@/lib/turn-illustration';
import { type DigitalPageBlock, linesToPlainText } from './types';

// ============================================================================
// Types
// ============================================================================

interface TurnArtInput {
  pageId: string;
  pageNumber: number;
  blockIndex: number;
  text: string;
  sceneDescription: string | null;
  originalImageUrl: string | null;
  turnArtUrl: string | null;
  hasIllustration: boolean;
}

interface TurnArtEditorProps {
  bookId: string;
  bookTitle: string;
  blocks: DigitalPageBlock[];
  onArtUpdated: () => void;
  /** When true, use dark theme (scan agent modal) */
  variant?: 'dark' | 'light';
}

// ============================================================================
// Component
// ============================================================================

export function TurnArtEditor({ bookId, bookTitle, blocks, onArtUpdated, variant = 'light' }: TurnArtEditorProps) {
  const isDark = variant === 'dark';
  // State
  const [inputs, setInputs] = useState<TurnArtInput[]>([]);
  const [storyBible, setStoryBible] = useState<BookStoryBible | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedPage, setExpandedPage] = useState<number | null>(null);
  const [editingDescription, setEditingDescription] = useState<number | null>(null);
  const [editedDescriptions, setEditedDescriptions] = useState<Record<number, string>>({});
  const [regenerating, setRegenerating] = useState<Set<number>>(new Set());
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [showStoryBible, setShowStoryBible] = useState(false);
  const [refreshingBible, setRefreshingBible] = useState(false);

  // ============================================================================
  // Data Loading
  // ============================================================================

  const loadInputs = useCallback(async () => {
    try {
      setLoading(true);

      // Get all source page IDs from blocks
      const allSourceIds = blocks.flatMap(b => b.sourcePageIds);
      
      // Fetch scanned pages data
      const { data: pages } = await supabase
        .from('scanned_book_pages')
        .select('id, page_number, extracted_text, illustration_description, image_url, enhanced_image_url, has_illustration')
        .in('id', allSourceIds.length > 0 ? allSourceIds : ['__none__']);

      const pageMap = new Map(pages?.map(p => [p.id, p]) || []);

      // Build inputs array matching blocks
      const inputsData: TurnArtInput[] = blocks.map((block, idx) => {
        const sourcePageId = block.sourcePageIds[0];
        const sourcePage = sourcePageId ? pageMap.get(sourcePageId) : null;
        
        return {
          pageId: sourcePageId || '',
          pageNumber: block.pageNumber,
          blockIndex: idx,
          text: linesToPlainText(block.lines),
          sceneDescription: sourcePage?.illustration_description || null,
          originalImageUrl: sourcePage?.image_url || null,
          turnArtUrl: sourcePage?.enhanced_image_url || null,
          hasIllustration: sourcePage?.has_illustration || false,
        };
      });

      setInputs(inputsData);

      // Load Story Bible
      const bible = await getOrCreateStoryBible(
        bookId,
        bookTitle,
        blocks.map((b, i) => ({
          pageNumber: i + 1,
          text: linesToPlainText(b.lines),
        }))
      );
      setStoryBible(bible);

    } catch (err) {
      console.error('[TurnArtEditor] Failed to load inputs:', err);
    } finally {
      setLoading(false);
    }
  }, [bookId, bookTitle, blocks]);

  useEffect(() => {
    loadInputs();
  }, [loadInputs]);

  // ============================================================================
  // Actions
  // ============================================================================

  const handleEditDescription = (index: number, value: string) => {
    setEditedDescriptions(prev => ({ ...prev, [index]: value }));
  };

  const handleSaveDescription = async (index: number) => {
    const input = inputs[index];
    const newDescription = editedDescriptions[index];
    
    if (!input.pageId || newDescription === undefined) return;

    try {
      // Update in database
      await supabase
        .from('scanned_book_pages')
        .update({ illustration_description: newDescription })
        .eq('id', input.pageId);

      // Update local state
      setInputs(prev => prev.map((inp, i) => 
        i === index ? { ...inp, sceneDescription: newDescription } : inp
      ));
      
      setEditingDescription(null);
      delete editedDescriptions[index];
      setEditedDescriptions({ ...editedDescriptions });
      
    } catch (err) {
      console.error('[TurnArtEditor] Failed to save description:', err);
    }
  };

  const handleRegenerateSingle = async (index: number) => {
    const input = inputs[index];
    if (!input.pageId) return;

    setRegenerating(prev => new Set(prev).add(index));

    try {
      // Get original image for reference if available
      let originalImageBase64: string | undefined;
      if (input.originalImageUrl) {
        try {
          const response = await fetch(input.originalImageUrl);
          if (response.ok) {
            const blob = await response.blob();
            originalImageBase64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          }
        } catch (e) {
          console.warn('[TurnArtEditor] Could not load original image');
        }
      }

      // Generate new Turn Art
      const result = await generateTurnIllustration({
        pageText: input.text,
        pageNumber: input.pageNumber,
        sceneDescription: input.sceneDescription || undefined,
        originalImageBase64,
        bookTitle,
        storyBible: storyBible || undefined,
      });

      // Upload to storage
      const imageBlob = await fetch(result.imageUrl).then(r => r.blob());
      const fileName = `${bookId}/turn-art-${input.pageNumber}-${Date.now()}.png`;
      
      const { error: uploadError } = await supabase.storage
        .from('turn-art')
        .upload(fileName, imageBlob, { contentType: 'image/png', upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('turn-art')
        .getPublicUrl(fileName);

      // Update database
      await supabase
        .from('scanned_book_pages')
        .update({ enhanced_image_url: publicUrl })
        .eq('id', input.pageId);

      // Update local state
      setInputs(prev => prev.map((inp, i) => 
        i === index ? { ...inp, turnArtUrl: publicUrl } : inp
      ));

      onArtUpdated();

    } catch (err) {
      console.error('[TurnArtEditor] Regenerate failed:', err);
    } finally {
      setRegenerating(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const handleRegenerateAll = async () => {
    setRegeneratingAll(true);

    for (let i = 0; i < inputs.length; i++) {
      await handleRegenerateSingle(i);
      // Small delay between generations
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setRegeneratingAll(false);
  };

  const handleRefreshStoryBible = async () => {
    setRefreshingBible(true);
    try {
      clearStoryBibleCache(bookId);
      const bible = await getOrCreateStoryBible(
        bookId,
        bookTitle,
        blocks.map((b, i) => ({
          pageNumber: i + 1,
          text: linesToPlainText(b.lines),
        }))
      );
      setStoryBible(bible);
    } finally {
      setRefreshingBible(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${isDark ? 'text-white/60' : ''}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-[#5E5CE6]' : 'text-purple-500'}`} />
      </div>
    );
  }

  // When Turn Art generation is disabled, show a notice instead of the full editor
  if (!FEATURES.TURN_ART_GENERATION) {
    return (
      <div className="space-y-4">
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-amber-50 border-amber-200'}`}>
          <AlertCircle className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-[#FF9F0A]' : 'text-amber-600'}`} />
          <div>
            <p className={`font-medium ${isDark ? 'text-white' : 'text-amber-900'}`}>Turn Art generation is disabled</p>
            <p className={`text-sm ${isDark ? 'text-white/50' : 'text-amber-700'}`}>
              Illustration generation has been turned off. Existing illustrations are preserved. 
              Re-enable in <code className="font-mono text-xs">lib/feature-flags.ts</code>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-purple-100'}`}>
            <Wand2 className={`w-5 h-5 ${isDark ? 'text-[#5E5CE6]' : 'text-purple-600'}`} />
          </div>
          <div>
            <h3 className={isDark ? 'font-semibold text-white' : 'font-semibold text-gray-900'}>Turn Art Inputs</h3>
            <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{inputs.length} pages · Edit descriptions to guide generation</p>
          </div>
        </div>

        <button
          onClick={handleRegenerateAll}
          disabled={regeneratingAll}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium disabled:opacity-50 transition-colors ${isDark ? 'bg-[#5E5CE6] text-white hover:brightness-110' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
        >
          {regeneratingAll ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Regenerate All
        </button>
      </div>

      {/* Story Bible */}
      <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-white/10 bg-white/5' : 'border-purple-200 bg-purple-50/50'}`}>
        <button
          onClick={() => setShowStoryBible(!showStoryBible)}
          className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-purple-100/50'}`}
        >
          <div className="flex items-center gap-2">
            {showStoryBible ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span className={`font-medium ${isDark ? 'text-white' : 'text-purple-900'}`}>Story Bible</span>
            <span className={`text-sm ${isDark ? 'text-white/50' : 'text-purple-600'}`}>
              ({storyBible?.characters.length || 0} characters)
            </span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); handleRefreshStoryBible(); }}
            disabled={refreshingBible}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-purple-200 text-purple-600'}`}
            title="Refresh Story Bible"
          >
            <RefreshCw className={`w-4 h-4 ${refreshingBible ? 'animate-spin' : ''}`} />
          </button>
        </button>

        {showStoryBible && storyBible && (
          <div className="px-4 pb-4 space-y-3">
            {storyBible.characters.length > 0 && (
              <div>
                <div className={`flex items-center gap-2 text-sm font-medium mb-2 ${isDark ? 'text-white/80' : 'text-purple-800'}`}>
                  <Users className="w-4 h-4" />
                  Characters
                </div>
                <div className="space-y-2">
                  {storyBible.characters.map((char, i) => (
                    <div key={i} className={`rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-white/5 text-white/90' : 'bg-white text-gray-600'}`}>
                      <span className="font-medium">{char.name}:</span>{' '}
                      <span className={isDark ? 'text-white/70' : ''}>{char.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className={`flex items-center gap-2 text-sm font-medium mb-2 ${isDark ? 'text-white/80' : 'text-purple-800'}`}>
                <MapPin className="w-4 h-4" />
                Setting
              </div>
              <div className={`rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-white/5 text-white/70' : 'bg-white text-gray-600'}`}>
                {storyBible.setting}
              </div>
            </div>

            <div>
              <div className={`flex items-center gap-2 text-sm font-medium mb-2 ${isDark ? 'text-white/80' : 'text-purple-800'}`}>
                <Palette className="w-4 h-4" />
                Mood
              </div>
              <div className={`rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-white/5 text-white/70' : 'bg-white text-gray-600'}`}>
                {storyBible.mood}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Page Inputs */}
      <div className="space-y-2">
        {inputs.map((input, idx) => (
          <div
            key={idx}
            className={`rounded-xl border overflow-hidden ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}
          >
            {/* Page Header */}
            <button
              onClick={() => setExpandedPage(expandedPage === idx ? null : idx)}
              className={`w-full px-4 py-3 flex items-center gap-4 transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
            >
              {/* Turn Art Thumbnail */}
              <div className={`w-12 h-16 rounded-lg overflow-hidden flex-shrink-0 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                {input.turnArtUrl ? (
                  <img src={input.turnArtUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className={`w-5 h-5 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Page {input.pageNumber}</span>
                  {input.turnArtUrl && (
                    <Check className="w-4 h-4 text-[#30D158]" />
                  )}
                  {regenerating.has(idx) && (
                    <Loader2 className={`w-4 h-4 animate-spin ${isDark ? 'text-[#5E5CE6]' : 'text-purple-500'}`} />
                  )}
                </div>
                <p className={`text-sm line-clamp-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {input.sceneDescription || input.text.slice(0, 100) || 'No description'}
                </p>
              </div>

              {/* Expand icon */}
              {expandedPage === idx ? (
                <ChevronDown className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              ) : (
                <ChevronRight className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              )}
            </button>

            {/* Expanded Details */}
            {expandedPage === idx && (
              <div className={`px-4 pb-4 space-y-4 border-t pt-4 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                {/* Images Row */}
                <div className="flex gap-4">
                  {/* Original Image */}
                  <div className="flex-1">
                    <div className={`text-xs font-medium mb-2 flex items-center gap-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      <Eye className="w-3 h-3" />
                      Original Scan
                    </div>
                    <div className={`aspect-[3/4] rounded-lg overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                      {input.originalImageUrl ? (
                        <img src={input.originalImageUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                          <span className="text-sm">No image</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Turn Art */}
                  <div className="flex-1">
                    <div className={`text-xs font-medium mb-2 flex items-center gap-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      <Wand2 className="w-3 h-3" />
                      Turn Art
                    </div>
                    <div className={`aspect-[3/4] rounded-lg overflow-hidden ${isDark ? 'bg-[#5E5CE6]/20' : 'bg-gradient-to-br from-purple-100 to-pink-100'}`}>
                      {input.turnArtUrl ? (
                        <img src={input.turnArtUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center ${isDark ? 'text-[#5E5CE6]' : 'text-purple-400'}`}>
                          <span className="text-sm">Not generated</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Page Text */}
                <div>
                  <div className={`text-xs font-medium mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Page Text</div>
                  <div className={`text-sm rounded-lg p-3 max-h-24 overflow-auto ${isDark ? 'bg-white/5 text-white/70' : 'bg-gray-50 text-gray-700'}`}>
                    {input.text || <span className={isDark ? 'text-white/40 italic' : 'text-gray-400 italic'}>No text</span>}
                  </div>
                </div>

                {/* Scene Description (Editable) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className={`text-xs font-medium ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Scene Description</div>
                    {editingDescription !== idx ? (
                      <button
                        onClick={() => {
                          setEditingDescription(idx);
                          setEditedDescriptions(prev => ({
                            ...prev,
                            [idx]: input.sceneDescription || ''
                          }));
                        }}
                        className={`text-xs flex items-center gap-1 ${isDark ? 'text-[#0A84FF] hover:text-[#0A84FF]/80' : 'text-purple-600 hover:text-purple-700'}`}
                      >
                        <Edit2 className="w-3 h-3" />
                        Edit
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSaveDescription(idx)}
                          className={`text-xs flex items-center gap-1 ${isDark ? 'text-[#30D158] hover:text-[#30D158]/80' : 'text-green-600 hover:text-green-700'}`}
                        >
                          <Save className="w-3 h-3" />
                          Save
                        </button>
                        <button
                          onClick={() => setEditingDescription(null)}
                          className={`text-xs flex items-center gap-1 ${isDark ? 'text-white/50 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          <X className="w-3 h-3" />
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  {editingDescription === idx ? (
                    <textarea
                      value={editedDescriptions[idx] || ''}
                      onChange={(e) => handleEditDescription(idx, e.target.value)}
                      placeholder="Describe what the illustration should show..."
                      className={`w-full p-3 text-sm rounded-lg focus:outline-none focus:ring-2 resize-none ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-white/30 focus:ring-[#0A84FF]/50' : 'border border-purple-200 focus:ring-purple-500'}`}
                      rows={3}
                    />
                  ) : (
                    <div className={`text-sm rounded-lg p-3 ${isDark ? 'bg-white/5 text-white/70' : 'bg-gray-50 text-gray-700'}`}>
                      {input.sceneDescription || (
                        <span className={isDark ? 'text-white/40 italic' : 'text-gray-400 italic'}>
                          No description · Will be auto-generated from text
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Regenerate Button */}
                <button
                  onClick={() => handleRegenerateSingle(idx)}
                  disabled={regenerating.has(idx)}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium disabled:opacity-50 transition-colors ${isDark ? 'bg-[#5E5CE6]/20 text-[#5E5CE6] hover:bg-[#5E5CE6]/30' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}
                >
                  {regenerating.has(idx) ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Regenerate Turn Art
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TurnArtEditor;
